import {
    Box,
    Text,
    Flex,
    Button,
    useDisclosure,
    HStack,
    Icon,
} from '@chakra-ui/react'
import { addMinutes, format } from 'date-fns'
import React, { useState } from 'react'
import { FiAlertTriangle } from 'react-icons/fi'
import ConfirmModal from '~components/appointments/confirm-modal'
import ManualEntryReviewDrawer from '~components/pending-actions/pending-item-rows/pending-appointment-row/manual-entry-review'
import { ModalType } from '~components/shared/confirm-modal'
import AppointmentDetailsDrawer from '~components/shared/modals/appointment-details/appointment-details-drawer'
import { useClinic } from '~contexts/clinic-context'
import { usePendingAppointments } from '~contexts/pending-appointments-context'
import {
    ActionItemFieldsFragment,
    ActionItemStatus,
    ActionItemType,
    AppointmentEntryMethod,
    FilterOp,
    ResolveReason,
    useCountActionItemsQuery,
    useMarkAppointmentAsResolvedMutation,
} from '~graphql/generated/graphql'
import { formatClinicianName, stringIfExists } from '~utils/helpers'
import { useGenericToasts } from '~utils/hooks/use-toast-feedback'
import { useActionItems } from '../../../../contexts/action-item-provider'
import { SharedAssignee, SharedTag } from './shared'

export default function PendingAppointmentContent({
    actionItem,
}: {
    actionItem: ActionItemFieldsFragment
}) {
    const { refetch } = useActionItems()
    const { clinic } = useClinic()
    const isOnManualEntry =
        clinic?.settings?.appointment_entry_method ===
        AppointmentEntryMethod.ManualEntry
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [shouldHide, setShouldHide] = useState<boolean>(false)

    const {
        onOpen: onDetailsOpen,
        onClose: onDetailsClose,
        isOpen: isDetailsOpen,
    } = useDisclosure()
    const {
        onOpen: onConfirmOpen,
        onClose: onConfirmClose,
        isOpen: isConfirmOpen,
    } = useDisclosure()
    const {
        onOpen: onManualEntryOpen,
        onClose: onManualEntryClose,
        isOpen: isManualEntryOpen,
    } = useDisclosure()
    const { onSuccess, onError } = useGenericToasts()
    const { confirmAppointment, requestChange } = usePendingAppointments()

    const [markAsResolved, { loading: resolveLoading }] =
        useMarkAppointmentAsResolvedMutation({
            onCompleted: refetch,
        })

    const { data: actionItemsCount } = useCountActionItemsQuery({
        variables: {
            where: [
                {
                    column: 'pending_appointment.id',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: actionItem.pending_appointment?.id,
                    },
                },
                {
                    column: 'type',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: ActionItemType.MissingClient,
                    },
                },
                {
                    column: 'status',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: ActionItemStatus.Pending,
                    },
                },
            ],
        },
        skip: !actionItem.pending_appointment,
    })

    if (!actionItem.pending_appointment || shouldHide) {
        return (
            <Box mt={1}>
                <Text
                    fontSize="sm"
                    color="gray.500"
                    fontStyle="italic"
                    fontWeight="semibold"
                >
                    This appointment has been resolved!
                </Text>
            </Box>
        )
    }

    const handleConfirmAppointment = async () => {
        setIsLoading(true)
        const isSuccess = await confirmAppointment(
            actionItem.pending_appointment!.id,
        )
        if (isSuccess) {
            refetch()
            setShouldHide(true)
        }
        setIsLoading(false)
        onConfirmClose()
    }

    const handleRequestChange = async (
        isDenial: boolean,
        reasonForDenial: string,
    ) => {
        setIsLoading(true)
        const isSuccess = await requestChange(
            actionItem.pending_appointment!,
            isDenial,
            reasonForDenial,
        )
        if (isSuccess) {
            onSuccess(
                isDenial
                    ? 'Successfully denied appointment'
                    : 'Successfully requested appointment change.',
            )
            refetch()
            setShouldHide(true)
        } else {
            onError(
                isDenial
                    ? 'Problem denying appointment'
                    : 'Problem requesting appointment change.',
            )
        }
        setIsLoading(false)
        // onClose()
    }

    const {
        service,
        service_name,
        clinician,
        client,
        patient,
        start_time,
        minutes,
    } = actionItem.pending_appointment

    return (
        <Box w="full">
            <Flex
                mt={1}
                justify="space-between"
                alignItems="center"
                direction="row"
                gap={3}
            >
                <Flex justify="space-between">
                    <Box>
                        <Text fontSize="md" fontWeight="semibold">
                            {service?.name || service_name}{' '}
                            {stringIfExists(formatClinicianName(clinician), {
                                pretext: 'with ',
                            })}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            {stringIfExists(client?.first_name, {
                                posttext:
                                    stringIfExists(client?.last_name, {
                                        pretext: ' ',
                                    }) + ', ',
                            })}
                            {stringIfExists(patient?.name, {
                                posttext: stringIfExists(
                                    patient?.clinic_species?.species,
                                    { pretext: ' (', posttext: ')' },
                                ),
                            })}
                            {!!patient && ', '}
                            {format(start_time, 'h:mm a')} -{' '}
                            {format(
                                addMinutes(start_time, minutes),
                                "h:mm a 'on' MMMM d",
                            )}
                        </Text>
                    </Box>
                </Flex>
                {actionItemsCount?.countActionItems &&
                actionItemsCount?.countActionItems > 0 ? (
                    <HStack maxW="320px" spacing={3}>
                        <Icon
                            as={FiAlertTriangle}
                            fontSize="lg"
                            color="gray.500"
                        />
                        <Text fontSize="xs" color="gray.600">
                            A valid client and patient must be assigned before
                            this appointment can be confirmed.
                        </Text>
                    </HStack>
                ) : (
                    <Flex
                        w="max-content"
                        direction="column"
                        alignItems="flex-end"
                        gap={2}
                    >
                        <Button size="xs" onClick={onDetailsOpen} w="full">
                            View Details
                        </Button>
                        <Button
                            size="xs"
                            colorScheme="brand"
                            onClick={() => {
                                isOnManualEntry
                                    ? onManualEntryOpen()
                                    : onConfirmOpen()
                            }}
                        >
                            Review & Confirm
                        </Button>
                    </Flex>
                )}
            </Flex>
            <ManualEntryReviewDrawer
                appointment={actionItem.pending_appointment}
                isOpen={isManualEntryOpen}
                onClose={onManualEntryClose}
                onConfirm={async () => {
                    await markAsResolved({
                        variables: {
                            id: actionItem.pending_appointment?.id || '',
                            reason: ResolveReason.EnteredManually,
                        },
                    })
                }}
                isLoading={isLoading || resolveLoading}
            />
            <AppointmentDetailsDrawer
                appointment={actionItem.pending_appointment}
                isOpen={isDetailsOpen}
                onClose={onDetailsClose}
            />
            <ConfirmModal
                onConfirm={async () => await handleConfirmAppointment()}
                onRequestChange={handleRequestChange}
                isLoading={isLoading}
                isOpen={isConfirmOpen}
                onClose={onConfirmClose}
                type={ModalType.Confirm}
                action="confirm"
                appointment={actionItem.pending_appointment}
            />
        </Box>
    )
}
